import React, {useState} from "react";
import {Alert, Button, Card, Spinner, Table} from "react-bootstrap";
import {PiHardDrives, PiTrash} from "react-icons/pi";
import useStatisticsData from "../Statistics/useStatisticsData.js";
import {formatNumber} from "../Statistics/format.js";
import {formatDateTimeDE} from "./format.js";
import HelpTooltip from "../HelpTooltip.jsx";
import NutzungAufraeumenDialog from "./NutzungAufraeumenDialog.jsx";

const SPEICHER_URL = "/nutzung/daten/speicher";

const TABELLEN_LABEL = {
    activity_events: "Ereignisse (Rohdaten)",
    activity_sessions: "Sitzungen",
    activity_daily: "Tagesverdichtung",
    activity_changes: "Datenänderungen (Rohdaten)",
    activity_agents: "Browser-/Geräte-Nachschlagetabelle",
};

/** 1.234.567 Byte -> "1,2 MB" - eine Schätzung, deshalb bewusst grob
 * (eine Nachkommastelle), nie mehr Genauigkeit vortäuschen als vorhanden. */
function formatBytes(bytes) {
    if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) {
        return "–";
    }
    const wert = Number(bytes);
    if (wert < 1024) {
        return `${formatNumber(wert)} B`;
    }
    const einheiten = ["KB", "MB", "GB", "TB"];
    let index = -1;
    let rest = wert;
    do {
        rest /= 1024;
        index += 1;
    } while (rest >= 1024 && index < einheiten.length - 1);
    return `${new Intl.NumberFormat("de-DE", {minimumFractionDigits: 1, maximumFractionDigits: 1}).format(rest)} ${einheiten[index]}`;
}

/**
 * Speicherplatz- und Aufräumbereich der Nutzungsstatistik (Spezifikation
 * Abschnitt 10) - Thorstens ausdrückliche Vorgabe: Aufbewahrung UNBEGRENZT
 * als Standard, aber jederzeit transparent aufräumbar. Deshalb wird VOR
 * jedem Löschvorgang gezeigt, wie viele Zeilen und wie viel Platz betroffen
 * sind - und erst danach folgt der eigentliche Lösch-Knopf mit eigener
 * Sicherheitsabfrage (siehe NutzungAufraeumenDialog).
 */
export default function NutzungSpeicherUebersicht() {
    const {data, loading, error, refetch} = useSpeicherDaten();
    const [ausgewaehlt, setAusgewaehlt] = useState(null);
    const [dialogOffen, setDialogOffen] = useState(false);

    const tabellen = data?.tabellen || [];
    const vorschau = data?.vorschau || [];
    const aktivEintrag = vorschau.find((v) => v.aelter_als_tage === ausgewaehlt) || null;

    return (
        <Card className="shadow-sm">
            <Card.Header className="d-flex align-items-center gap-2">
                <PiHardDrives size={20} aria-hidden="true" />
                <span className="fw-semibold">Speicherplatz und Aufräumen</span>
                <HelpTooltip placement="right" size={0.85}>
                    Standard ist unbegrenzte Aufbewahrung - hier lässt sich jederzeit transparent
                    nachvollziehen, wie viel Platz die Statistik belegt und bei Bedarf gezielt
                    aufräumen. Die Tagesverdichtung bleibt dabei immer erhalten.
                </HelpTooltip>
            </Card.Header>
            <Card.Body>
                {loading && (
                    <div className="d-flex align-items-center gap-2 text-muted">
                        <Spinner animation="border" size="sm" />
                        Wird geladen…
                    </div>
                )}

                {error && <Alert variant="danger" className="mb-0">{error}</Alert>}

                {!loading && !error && data && (
                    <>
                        <div className="row g-3 mb-3">
                            <div className="col-sm-6 col-lg-3">
                                <div className="small text-muted">Belegter Speicher gesamt</div>
                                <div className="fs-4 fw-semibold">{formatBytes(data.gesamt_bytes)}</div>
                            </div>
                            <div className="col-sm-6 col-lg-3">
                                <div className="small text-muted">Daten liegen vor seit</div>
                                <div className="fs-6">{data.aelteste ? formatDateTimeDE(data.aelteste) : "–"}</div>
                            </div>
                        </div>

                        <div style={{overflowX: "auto"}}>
                            <Table size="sm" className="align-middle mb-4">
                                <thead>
                                    <tr>
                                        <th>Tabelle</th>
                                        <th className="text-end">Zeilen</th>
                                        <th className="text-end">Speicherplatz</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tabellen.map((tabelle) => (
                                        <tr key={tabelle.name}>
                                            <td>{TABELLEN_LABEL[tabelle.name] || tabelle.name}</td>
                                            <td className="text-end">{formatNumber(tabelle.zeilen)}</td>
                                            <td className="text-end">{formatBytes(tabelle.bytes)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        <div className="fw-semibold mb-2">Aufräumen</div>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {vorschau.map((eintrag) => (
                                <button
                                    key={eintrag.aelter_als_tage}
                                    type="button"
                                    aria-pressed={ausgewaehlt === eintrag.aelter_als_tage}
                                    className={`btn btn-sm rounded-pill ${
                                        ausgewaehlt === eintrag.aelter_als_tage ? "btn-primary" : "btn-outline-secondary"
                                    }`}
                                    onClick={() => setAusgewaehlt(eintrag.aelter_als_tage)}
                                >
                                    Älter als {eintrag.aelter_als_tage} Tage
                                </button>
                            ))}
                        </div>

                        {aktivEintrag && (
                            <Alert variant="light" className="border d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <div>
                                    Betrifft <strong>{formatNumber(aktivEintrag.zeilen)}</strong> Zeilen bis{" "}
                                    {formatDateTimeDE(aktivEintrag.bis_datum)} - rund{" "}
                                    <strong>{formatBytes(aktivEintrag.bytes)}</strong> werden dabei freigegeben (Schätzung).
                                </div>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    disabled={aktivEintrag.zeilen === 0}
                                    onClick={() => setDialogOffen(true)}
                                >
                                    <PiTrash className="me-1" aria-hidden="true" />
                                    Aufräumen…
                                </Button>
                            </Alert>
                        )}

                        <div className="small text-muted mt-3 mb-0">
                            Ohne Auswahl bleibt alles unverändert erhalten - die Aufbewahrung ist standardmäßig
                            unbegrenzt.
                        </div>
                    </>
                )}
            </Card.Body>

            <NutzungAufraeumenDialog
                show={dialogOffen}
                eintrag={aktivEintrag}
                formatBytes={formatBytes}
                onClose={() => setDialogOffen(false)}
                onErledigt={() => {
                    refetch();
                    setAusgewaehlt(null);
                }}
            />
        </Card>
    );
}

/**
 * useStatisticsData bietet kein refetch an (Vorbild aus Teil 1, hier nicht
 * verändert) - dieser kleine Wrapper erzwingt einen Neuabruf über einen
 * Zähler in den params-Objekt, ohne die geteilte Datei anzufassen.
 */
function useSpeicherDaten() {
    const [zaehler, setZaehler] = useState(0);
    const {data, loading, error} = useStatisticsData(SPEICHER_URL, {_: zaehler});
    return {data, loading, error, refetch: () => setZaehler((n) => n + 1)};
}
